import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Mail, MailOpen, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications');
      setItems(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((n) => filter === 'all' || n.status === filter);
  const counts = items.reduce((acc, n) => { acc[n.status] = (acc[n.status] || 0) + 1; return acc; }, {});
  const openedCount = items.filter(n => n.opened_at).length;

  return (
    <div className="max-w-7xl space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">
            Traçabilité HSE
          </div>
          <h1 className="text-4xl font-display font-black tracking-tighter">Tableau de bord notifications</h1>
          <p className="text-stone-600 mt-2 text-sm">
            Log de toutes les alertes envoyées aux donneurs d'ordre — qui, quand, quel permis, accusé de réception.
          </p>
        </div>
        <button onClick={load} className="btn-industrial-secondary flex items-center gap-2" data-testid="refresh-notifs">
          <RefreshCw className="w-4 h-4" /> Rafraîchir
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="section-card">
          <div className="label-industrial">Total envoyé</div>
          <div className="text-4xl font-display font-black mt-2">{items.length}</div>
        </div>
        <div className="section-card">
          <div className="label-industrial text-green-700">Envoyé réel</div>
          <div className="text-4xl font-display font-black mt-2 text-green-700">{counts.envoye || 0}</div>
        </div>
        <div className="section-card">
          <div className="label-industrial text-stone-500">Mock (Resend non configuré)</div>
          <div className="text-4xl font-display font-black mt-2 text-stone-500">{counts.mock || 0}</div>
        </div>
        <div className="section-card">
          <div className="label-industrial text-[#DA291C]">Ouvertures confirmées</div>
          <div className="text-4xl font-display font-black mt-2 text-[#DA291C]">{openedCount}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 text-xs">
        <span className="label-industrial !mb-0">Filtre :</span>
        {[
          { id: 'all', label: 'Tous' },
          { id: 'envoye', label: 'Envoyé' },
          { id: 'mock', label: 'Mock' },
          { id: 'echec', label: 'Échec' },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`notif-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 border-2 font-bold uppercase tracking-wider ${
              filter === f.id ? 'border-[#09090B] bg-[#09090B] text-white' : 'border-stone-200 text-stone-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="section-card p-0 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-stone-500">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <AlertCircle className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <div className="text-sm font-bold uppercase tracking-wider text-stone-500">
              Aucune notification {filter !== 'all' ? `(filtre: ${filter})` : ''}
            </div>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="notifications-table">
            <thead className="bg-stone-50 border-b-2 border-stone-200">
              <tr>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-4 py-3">Date</th>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-4 py-3">Permis</th>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-4 py-3">Destinataires</th>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-4 py-3">Type</th>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-4 py-3">Statut</th>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-4 py-3">Accusé réception</th>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((n) => (
                <tr key={n.id} data-testid={`notif-row-${n.id}`} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-3 text-xs font-mono text-stone-700">{(n.sent_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-stone-700">{n.permit_numero}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{n.permit_titre}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {(n.to || []).map((e, i) => (
                      <div key={i} className="text-stone-700">{e}</div>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-xs uppercase font-semibold text-stone-700">
                    {n.kind === 'auto_donneur_ordre' ? 'Auto' : 'Manuel'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${
                      n.status === 'envoye' ? 'bg-green-50 text-green-700 border-green-300' :
                      n.status === 'mock' ? 'bg-stone-100 text-stone-600 border-stone-300' :
                      'bg-red-50 text-red-700 border-red-300'
                    }`}>{n.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {n.opened_at ? (
                      <div className="flex items-center gap-1.5 text-green-700 text-xs">
                        <MailOpen className="w-4 h-4" />
                        <span>{(n.opened_at || '').slice(0, 16).replace('T', ' ')}</span>
                        {n.open_count > 1 && (
                          <span className="bg-green-700 text-white px-1.5 py-0.5 text-[10px] font-bold rounded-full">×{n.open_count}</span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-stone-400 text-xs">
                        <Mail className="w-4 h-4" />
                        <span>Non lu</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/permits/${n.permit_id}`)}
                      className="text-xs font-bold uppercase tracking-wider text-[#DA291C] inline-flex items-center gap-1 hover:underline"
                    >
                      Voir <ExternalLink className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
